import React from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';
import { selectRowColumn } from '../utils/columnHelpers';
const propTypes = {
  /**
   * Whether all rows are selected
   */
  allRowsSelected: PropTypes.bool,
  /**
   * The columns to be displayed in the header
   */
  columns: PropTypes.array.isRequired,
  /**
   * Function to handle the sort event
   *
   * ```
   * (dataKey: string, order: 'asc' | 'desc') => void
   * ```
   */
  onSortChange: PropTypes.func.isRequired,
  /**
   * Function to handle the select all event.
   *
   * ```
   * (checked: boolean) => void
   * ```
   */
  onToggleSelectAll: PropTypes.func,
  /**
   * The current sort state object.
   *
   * ```
   * { key: string, order: 'asc' | 'desc' }
   * ```
   */
  sortState: PropTypes.object
};
function getNextSortOrder(currentSort, dataKey) {
  // If nothing has been sorted, return desc
  if (!currentSort) return 'desc';
  if (currentSort.key === dataKey) {
    return currentSort.order === 'desc' ? 'asc' : 'desc';
  }

  // default to descending
  return 'desc';
}
function getInlineStylesForColumn(column) {
  const styles = {};
  if (column.width) {
    styles.width = column.width;
    styles.maxWidth = column.width;
  } else if (column.minWidth) {
    styles.flexBasis = column.minWidth;
  }
  return styles;
}
function GridListHeader(_ref) {
  let {
    columns,
    sortState,
    allRowsSelected,
    onToggleSelectAll,
    onSortChange
  } = _ref;
  const ariaSortValue = sortState && sortState.order === 'asc' ? 'ascending' : 'descending';
  return /*#__PURE__*/React.createElement("div", {
    className: "asc-grid-row asc-grid-header",
    role: "row"
  }, columns.map(column => {
    if (selectRowColumn.isSelectRowColumn(column)) {
      return /*#__PURE__*/React.createElement("div", {
        key: column.dataKey,
        className: "asc-grid-row-item asc-grid-row-item--select"
      }, /*#__PURE__*/React.createElement("div", {
        className: "asc-checkbox-group"
      }, /*#__PURE__*/React.createElement("input", {
        type: "checkbox",
        id: "select-all",
        name: "select-all",
        checked: allRowsSelected,
        onChange: event => onToggleSelectAll(event.target.checked)
      }), /*#__PURE__*/React.createElement("label", {
        htmlFor: "select-all",
        className: "asc-form-label"
      }, /*#__PURE__*/React.createElement("span", {
        className: "asc-acc-hidden"
      }, "Select all"))));
    }
    const nextSortOrder = getNextSortOrder(sortState, column.dataKey);
    const isActiveSort = sortState && sortState.key === column.dataKey;
    return /*#__PURE__*/React.createElement("div", {
      key: column.dataKey,
      className: cx('asc-grid-row-item', {
        'asc-text--right': column.textAlign === 'right',
        'asc-text--center': column.textAlign === 'center'
      }),
      style: getInlineStylesForColumn(column),
      "aria-sort": isActiveSort ? ariaSortValue : null
    }, column.sortable && /*#__PURE__*/React.createElement("button", {
      type: "button",
      className: cx('asc-grid-heading-sorter', isActiveSort && (nextSortOrder === 'asc' ? 'asc-grid-heading-sorter--asc' : 'asc-grid-heading-sorter--desc')),
      title: column.header,
      onClick: () => onSortChange(column.dataKey, nextSortOrder)
    }, column.header), !column.sortable && column.header);
  }));
}
GridListHeader.propTypes = propTypes;
export default GridListHeader;